import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '../lib/auth-client';
import { api } from '../lib/api';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

export function NotificationsBell() {
  const qc = useQueryClient();
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ data: Notification[] }>('/api/me/notifications'),
    enabled: !!session?.user,
    refetchInterval: 60_000,
  });

  const readAll = useMutation({
    mutationFn: () => api.post('/api/me/notifications/read-all', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  if (!session?.user) return null;

  const list = data?.data ?? [];
  const unread = list.filter((n) => !n.isRead).length;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 rounded-xl hover:bg-gray-100 flex items-center justify-center text-xl"
        title="الإشعارات"
      >
        🔔
        {unread > 0 && (
          <span className="absolute top-1 left-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-2 left-0 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-brand-xl z-40 border border-gray-100 overflow-hidden">
            <div className="bg-navy-darker text-white px-4 py-3 flex items-center justify-between">
              <h3 className="font-black">🔔 الإشعارات</h3>
              {unread > 0 && (
                <button onClick={() => readAll.mutate()} className="text-xs text-gold hover:underline">
                  تعليم الكل كمقروء
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {list.length === 0 ? (
                <div className="text-center py-12 text-ink-muted">
                  <div className="text-4xl mb-2">📭</div>
                  <p className="text-sm">لا توجد إشعارات</p>
                </div>
              ) : (
                list.slice(0, 10).map((n) => (
                  <Link
                    key={n.id}
                    to={n.link ?? '#'}
                    onClick={() => setOpen(false)}
                    className={`block px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition ${!n.isRead ? 'bg-blue-50/50' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.isRead && <span className="w-2 h-2 mt-2 rounded-full bg-blue-500 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-sm">{n.title}</h4>
                        {n.body && <p className="text-xs text-ink-muted line-clamp-2 mt-1">{n.body}</p>}
                        <p className="text-[10px] text-ink-light mt-1">{new Date(n.createdAt).toLocaleString('ar-SA')}</p>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
            <div className="bg-gray-50 px-4 py-2 text-center">
              <Link to="/dashboard/notifications" onClick={() => setOpen(false)} className="text-xs text-navy-dark hover:underline font-bold">
                عرض الكل →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
